import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { withAuthenticationRequired } from '@auth0/auth0-react';

import { getLocal, sendAction, updateProfile } from '../../utils/api';

import './Onboarding.css';

function Onboarding({ handleError }) {
  const GOAL_LIST = ['I Have No Idea!', 'College', 'Job Training', 'Military', 'Into a Career', 'Other'];
  const STATUS_LIST = ['High School', 'College', 'Other'];
  const SLIDE_INTERVAL = 3000;
  const SLIDE_STOP = 5;

  const navigate = useNavigate();
  const inputRefName = useRef(null);
  const inputRefSchool = useRef(null);
  const inputRefGoalInfo = useRef(null);

  const [isRunning, setIsRunning] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [profile, setProfile] = useState(getLocal('profile'));
  const [slide, setSlide] = useState(0);

  const checkProgress = useCallback(() => {
    if (profile?.meta_data?.find(param => param.param === 'goal_info' && param.value !== undefined)) {
      navigate('/guide');
    } else if (profile?.meta_data?.find(param => param.param === 'goal' && param.value !== undefined)) {
      setSlide(10);
    } else if (profile?.meta_data?.find(param => param.param === 'school' && param.value !== undefined)) {
      setSlide(9);
    } else if (profile?.meta_data?.find(param => param.param === 'status' && param.value !== undefined)) {
      setSlide(8);
    } else if (profile?.meta_data?.find(param => param.param === 'name' && param.value !== undefined)) {
      setSlide(7);
    } else {
      setSlide(1);
      
      setTimeout(() => {
        setIsRunning(true);
      }, SLIDE_INTERVAL);
    }
  }, [navigate]);

  useEffect(() => {
    checkProgress();
  }, [checkProgress]);

  useEffect(() => {
    if (!isRunning) return;

    setSlide(prevSlide => prevSlide + 1);

    const interval = setInterval(() => {
      setSlide(prevSlide => {
        if (prevSlide === SLIDE_STOP - 1) {
          clearInterval(interval);
          setIsRunning(false);
        }

        return prevSlide + 1;
      });
    }, SLIDE_INTERVAL);

    return () => clearInterval(interval);
  }, [isRunning]);
  
  const submitParam = useCallback(async (param, value) => {
    setIsSending(true);
  
    try {
      const response = await updateProfile(param, value);

      if (response !== null) {
        setProfile(response);
        
        if (param === 'goal_info') {
          await sendAction('onboarding');
          navigate('/guide');
        } else if (param === 'name' && value === null) {
          await sendAction('hello');
          setSlide(prevSlide => prevSlide + 2);
        } else {
          setSlide(prevSlide => prevSlide + 1);
  
          if (param === 'name') {
            await sendAction('hello');
            
            setTimeout(() => {
              setSlide(prevSlide => prevSlide + 1);
            }, SLIDE_INTERVAL);
          }
        }
      }
    } catch (e) {
      handleError(e);
    }

    setIsSending(false);
  }, [handleError, navigate]);

  const handleProfile = useCallback(async (param, e) => {
    let response;

    switch (param) {
      //Selection params
      case 'goal':
      case 'status':
        setIsSending(true);

        try {
          response = await updateProfile(param, e);
    
          if (response !== null) {
            setProfile(response);
            setSlide(prevSlide => prevSlide + 1);
          }
        } catch (e) {
          handleError(e);
        }
    
        setIsSending(false);
        break;
      //Free text params
      case 'goal_info':
      case 'name':
      case 'school':
        const value = e.target.value;

        if (e.keyCode === 13) {
          await submitParam(param, value.length ? value : null);
        }
        break;
      //Skip was clicked
      default:
        setIsSending(true);

        try {
          response = await updateProfile(e, null);

          if (response !== null) {
            setProfile(response);
  
            if (e === 'goal_info') {
              await sendAction('onboarding');
              navigate('/guide');
            } else if (e === 'status' || e === 'school' || e === 'goal') {
              setSlide(prevSlide => prevSlide + 1);
            } else if (e === 'name') {
              await sendAction('hello');
              setSlide(prevSlide => prevSlide + 2);
            }
          }
        } catch (e) {
          handleError(e);
        }

        setIsSending(false);
        break;
    }
  }, [handleError, navigate, submitParam]);

  const handleSubmit = useCallback(async (param) => {
    let value;

    if (param === 'name') {
      value = inputRefName.current.value;
    } else if (param === 'school') {
      value = inputRefSchool.current.value;
    } else if (param === 'goal_info') {
      value = inputRefGoalInfo.current.value;
    }

    if (value !== undefined) {
      await submitParam(param, value.length ? value : null);
    }
  }, [submitParam]);

  return (
    <div id="onboarding">
      <div id="slide0" className="slide" style={{ opacity: (slide === 0 ? 1 : 0), visibility: (slide === 0 ? 'visible' : 'hidden') }}>
        <div className="content"></div>
      </div>
      <div id="slide1" className="slide" onClick={() => setIsRunning(true)} style={{ opacity: (slide === 1 ? 1 : 0), visibility: (slide === 1 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>Hello</span>
        </div>
      </div>
      <div id="slide2" className="slide" style={{ opacity: (slide === 2 ? 1 : 0), visibility: (slide === 2 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>I’m Lexa, an AI guide to your future</span>
        </div>
      </div>
      <div id="slide3" className="slide" style={{ opacity: (slide === 3 ? 1 : 0), visibility: (slide === 3 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>Let’s explore it together!</span>
        </div>
      </div>
      <div id="slide4" className="slide" style={{ opacity: (slide === 4 ? 1 : 0), visibility: (slide === 4 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>First let me get to know you</span>
        </div>
      </div>
      <div id="slide5" className="slide" style={{ opacity: (slide === 5 ? 1 : 0), visibility: (slide === 5 ? 'visible' : 'hidden') }}>
        <div className="content">
          <div className="box">
            <span>What’s your name?</span>
            <div className="text-input">
              <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" />
              <input ref={inputRefName} type="input" name="onboarding" placeholder={isSending ? 'Give me a sec...' : 'Tap to respond'} autoComplete="off" disabled={isSending} onKeyDown={(e) => handleProfile('name', e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => handleSubmit('name')} />
            </div>
          </div>
          <div className="skip" onClick={() => handleProfile('skip', 'name')}>Skip</div>
        </div>
      </div>
      <div id="slide6" className="slide" style={{ opacity: (slide === 6 ? 1 : 0), visibility: (slide === 6 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>Hi {profile?.meta_data?.find(param => param.param === 'name' && param.value !== undefined)?.value}, it’s great to meet you!</span>
        </div>
      </div>
      <div id="slide7" className="slide question" style={{ opacity: (slide === 7 ? 1 : 0), visibility: (slide === 7 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>Where are you now?</span>
          <div className="scrollable">
            {STATUS_LIST.map((v, i) => (
              <div key={i} className="box" onClick={() => handleProfile('status', v)}>{v}</div>
            ))}
          </div>
          <div className="skip" onClick={() => handleProfile('skip', 'status')}>Skip</div>
        </div>
      </div>
      <div id="slide8" className="slide" style={{ opacity: (slide === 8 ? 1 : 0), visibility: (slide === 8 ? 'visible' : 'hidden') }}>
        <div className="content">
          <div className="box">
            <span>What school are you going to?</span>
            <div className="text-input">
              <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" />
              <input ref={inputRefSchool} type="input" name="school" placeholder={isSending ? 'Give me a sec...' : 'Tap to respond'} autoComplete="off" disabled={isSending} onKeyDown={(e) => handleProfile('school', e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => handleSubmit('school')} />
            </div>
          </div>
          <div className="skip" onClick={() => handleProfile('skip', 'school')}>Skip</div>
        </div>
      </div>
      <div id="slide9" className="slide question" style={{ opacity: (slide === 9 ? 1 : 0), visibility: (slide === 9 ? 'visible' : 'hidden') }}>
        <div className="content">
          <span>Where do you want to go?</span>
          <div className="scrollable">
            {GOAL_LIST.map((v, i) => (
              <div key={i} className="box" onClick={() => handleProfile('goal', v)}>{v}</div>
            ))}
          </div>
          <div className="skip" onClick={() => handleProfile('skip', 'goal')}>Skip</div>
        </div>
      </div>
      <div id="slide10" className="slide" style={{ opacity: (slide === 10 ? 1 : 0), visibility: (slide === 10 ? 'visible' : 'hidden') }}>
        <div className="content">
          <div className="box">
            <span>Can you tell me more about your goals (career, life, other)?</span>
            <div className="text-input">
              <img className="icon-chat" src={process.env.PUBLIC_URL + '/assets/icons/guide.svg'} alt="Chat" />
              <input ref={inputRefGoalInfo} type="input" name="goal" placeholder={isSending ? 'Give me a sec...' : 'Tap to respond'} autoComplete="off" disabled={isSending} onKeyDown={(e) => handleProfile('goal_info', e)} />
              <img className="icon-submit" src={process.env.PUBLIC_URL + '/assets/icons/submit.svg'} alt="Submit" onClick={() => handleSubmit('goal_info')} />
            </div>
          </div>
          <div className="skip" onClick={() => handleProfile('skip', 'goal_info')}>Skip</div>
        </div>
      </div>
    </div>
  );
}

export default withAuthenticationRequired(Onboarding);